import React from "react";
import { motion } from "framer-motion";
import {
  SectionBadge,
  IconChart,
  Highlight,
  FadeUp,
} from "@/components/BrandKit";
import { DoodleChart, DoodleStar, DoodleSparkle, DoodleCube } from "@/components/Doodles";

export default function Results() {
  return (
    <section
      id="results"
      data-testid="results-section"
      className="relative bg-[#0E0E0E] text-[#fdfbf7] border-y border-black bbs-noise overflow-hidden"
    >
      <div className="max-w-[1480px] mx-auto px-5 sm:px-8 py-20 sm:py-28 relative z-10">
        {/* Eyebrow */}
        <FadeUp>
          <div className="flex items-center justify-between">
            <SectionBadge
              label="Impact"
              num="03"
              color="#FFB703"
              icon={<IconChart className="w-4 h-4 text-black" />}
            />
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#fdfbf7]/50">
              Instamart × Big Bear
            </p>
          </div>
        </FadeUp>

        {/* Big statement */}
        <FadeUp delay={0.1}>
          <h2
            data-testid="results-heading"
            className="mt-6 font-display uppercase text-6xl sm:text-7xl lg:text-[128px] leading-[0.9] tracking-[-0.03em]"
            style={{ fontWeight: 900 }}
          >
            Impact.
          </h2>
        </FadeUp>

        {/* Client card + illustration */}
        <div className="mt-12 grid grid-cols-12 gap-8 lg:gap-14 items-start">
          {/* Left column - client detail */}
          <div className="col-span-12 lg:col-span-7">
            <FadeUp>
              <div
                className="inline-flex flex-col gap-7 border border-[#fdfbf7]/30 p-6 sm:p-8 bg-[#0B0B0B] overflow-hidden"
                style={{ boxShadow: "8px 8px 0 rgba(255,183,3,0.7)" }}
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB703]">
                  ◆ The Client
                </p>
                <div className="h-24 sm:h-28 lg:h-32 w-40 sm:w-52 lg:w-64 flex items-center justify-center overflow-hidden">
                  <img
                    src="/brand/logos/instamart-white.png"
                    alt="Swiggy Instamart"
                    className="object-contain"
                    style={{ transform: "scale(2.2)", maxHeight: "100%", maxWidth: "100%" }}
                  />
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.08}>
              <h3
                data-testid="results-subheading"
                className="mt-8 font-display uppercase text-2xl sm:text-3xl lg:text-[38px] leading-[1.12] tracking-tight max-w-2xl"
                style={{ fontWeight: 800 }}
              >
                Built the team&apos;s{" "}
                <Highlight color="#FFB703" textColor="#0E0E0E">
                  independent
                </Highlight>{" "}
                video capability from the ground up.
              </h3>
            </FadeUp>
            <FadeUp delay={0.14}>
              <p className="mt-6 max-w-xl text-[#fdfbf7]/75 text-base sm:text-lg leading-[1.6]">
                Scaled{" "}
                <span className="font-display text-[#FFB703]" style={{ fontWeight: 800 }}>
                  2,000+
                </span>{" "}
                AI, Live-action and Animation films across mediums.
              </p>
            </FadeUp>

            {/* Stats row */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-0 border-t border-[#fdfbf7]/20">
              <FadeUp>
                <div
                  data-testid="stat-performance"
                  className="py-8 sm:pr-8 border-b sm:border-b-0 sm:border-r border-[#fdfbf7]/20"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4D00] mb-3">
                    ▲ Higher CTR
                  </div>
                  <div className="flex items-baseline">
                    <span className="bbs-stat text-[#FF4D00] text-[96px] sm:text-[132px] lg:text-[152px]">
                      30
                    </span>
                    <span className="bbs-stat text-[#FF4D00] text-5xl sm:text-6xl lg:text-7xl">
                      %
                    </span>
                  </div>
                  <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#fdfbf7]/55">
                    vs baseline
                  </p>
                </div>
              </FadeUp>
              <FadeUp delay={0.1}>
                <div data-testid="stat-cpc" className="py-8 sm:pl-8">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB703] mb-3">
                    ▼ Lower CPC
                  </div>
                  <div className="flex items-baseline">
                    <span className="bbs-stat text-[#FFB703] text-[96px] sm:text-[132px] lg:text-[152px]">
                      40
                    </span>
                    <span className="bbs-stat text-[#FFB703] text-5xl sm:text-6xl lg:text-7xl">
                      %
                    </span>
                  </div>
                  <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#fdfbf7]/55">
                    vs baseline
                  </p>
                </div>
              </FadeUp>
            </div>
          </div>

          {/* Illustration */}
          <div className="col-span-12 lg:col-span-5 flex justify-center lg:justify-end">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -top-3 -left-3 w-14 h-14 bg-[#FFB703] rotate-6" />
              <div className="absolute -bottom-3 -right-3 w-16 h-16 bg-[#FF4D00] -rotate-6" />
              <div className="relative border border-[#fdfbf7]/25 bg-[#0B0B0B] p-6 sm:p-8">
                <div className="text-[#fdfbf7]">
                  <DoodleChart className="w-full max-w-[380px] h-auto" />
                </div>
              </div>

              <motion.div
                animate={{ y: [0, -8, 0], rotate: [0, 15, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-6 right-8 text-[#48B02C]"
              >
                <DoodleStar className="w-10 h-10" />
              </motion.div>
              <motion.div
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                className="absolute -bottom-8 left-6 text-[#FFB703]"
              >
                <DoodleSparkle className="w-8 h-8" />
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Running tape */}
        <div className="mt-16 -mx-5 sm:-mx-8 border-y border-[#fdfbf7]/20 overflow-hidden">
          <div className="bbs-marquee-wrap">
            <div
              className="bbs-marquee-track py-6 sm:py-8"
              style={{ animationDuration: "55s" }}
            >
              {Array.from({ length: 3 })
                .flatMap(() => [
                  "Context-aware",
                  "2,000+ films",
                  "Emotionally precise",
                  "Multiple languages",
                  "Visually fresh",
                  "Fast to land",
                  "Fun, no bar",
                ])
                .map((s, i) => (
                  <span
                    key={i}
                    className="font-display uppercase text-3xl sm:text-5xl tracking-tight px-8 inline-flex items-center text-[#fdfbf7]/95"
                    style={{ fontWeight: 800 }}
                  >
                    <span>{s}</span>
                    <span className="text-[#FFB703] mx-8">
                      <DoodleCube className="w-8 h-8 inline-block" />
                    </span>
                  </span>
                ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
